import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
} from "@mui/material";
import axios from "../../api";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import SoftButton from "components/SoftButton";
import { useSelector } from "react-redux";
import StatusTracker from "examples/ReuseFunctions/StatusTracker";
import { RootState } from "store";
import DateFormat from "examples/ReuseFunctions/DateFormat";
import { AxiosError } from "axios";

const validationSchema = Yup.object({
  status_id: Yup.number().required("Status is required"),
  pending_reason: Yup.string().nullable(),
  onhold_reason: Yup.string().nullable(),
});

interface Media {
  multimedia_id: number;
  uploaded_by: string;
  media_stage: string | null;
  file_name: string;
  file_type: string;
  file_path: string;
  latitude: number;
  longitude: number;
}

interface EmployeeTicket {
  multimedia?: Media[] | null;
  status_id: number;
  pending_reason: string | null;
  onhold_reason: string | null;
  employee_name: string;
  employee_phone: string;
  status_tracker: string[] | string | null;
  ticket_id: number;
}

interface EmployeeEditProps {
  onClose: () => void;
  employeeTicketData: EmployeeTicket | null;
  fetchData: () => Promise<void>;
}

interface FormValues {
  status_id: number;
  pending_reason: string | null;
  onhold_reason?: string | null;
}

interface TicketData {
  status_id?: number;
  pending_reason: string | null;
  status_tracker: any;
}

const EmployeeEditTicket: React.FC<EmployeeEditProps> = ({
  onClose,
  employeeTicketData,
  fetchData,
}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const { fetchError, fetchSuccess } = useSnackbar();

  const hasPreUploads = employeeTicketData?.multimedia?.some(
    (m) => m.media_stage === "pre"
  );
  const currentTime = DateFormat();

  // Fixed: Helper function to get status message based on status_id
  const getStatusMessage = (
    statusId: number,
    pendingReason: string,
    onHoldReason: string
  ): string => {
    if (statusId === 4) {
      return onHoldReason;
    }
    if (statusId === 5) {
      return pendingReason;
    }
    return "Ticket is completed";
  };

  // Fixed: Helper function to get status name based on status_id
  const getStatusName = (statusId: number): string => {
    if (statusId === 4) {
      return "On-hold";
    }
    if (statusId === 5) {
      return "Pending";
    }
    return "Done";
  };

  return (
    <Formik<FormValues>
      initialValues={{
        status_id: employeeTicketData?.status_id || 3,
        pending_reason: employeeTicketData?.pending_reason || null,
        onhold_reason: null,
      }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        const onHoldReason = values?.onhold_reason || "On-Hold from Employee";
        const pendingReason = values?.pending_reason || "Pending from Employee";

        // Create a copy of values without onhold_reason
        const { onhold_reason, ...valuesWithoutOnHold } = values;

        // Build ticketData object conditionally
        const ticketData: TicketData = {
          pending_reason: valuesWithoutOnHold.pending_reason,
          status_tracker: null, // Will be set below
        };

        // Only include status_id if it changed
        if (values.status_id !== employeeTicketData?.status_id) {
          ticketData.status_id = values.status_id;
        }

        // Fixed: Use helper functions instead of nested ternary
        const trackerData = StatusTracker({
          previousData: Array.isArray(employeeTicketData?.status_tracker)
            ? employeeTicketData.status_tracker.join(",")
            : employeeTicketData?.status_tracker ?? null,
          message: getStatusMessage(
            values.status_id,
            pendingReason,
            onHoldReason
          ),
          statusName: getStatusName(values.status_id),
          status_id: values.status_id,
          createdby: user?.name ?? "",
          employeeName: user?.name ?? employeeTicketData?.employee_name ?? "",
          employeePhone: employeeTicketData?.employee_phone ?? "",
          arraivalDate: currentTime,
        });

        ticketData.status_tracker = trackerData;

        try {
          const response = await axios.put(
            `/tickets/${employeeTicketData?.ticket_id}`,
            { ticketData }
          );
          fetchData();
          onClose();
          fetchSuccess(response?.data?.message);
        } catch (error) {
          if (error instanceof AxiosError && error.response) {
            fetchError(error.response.data.error);
          }
        }
      }}
    >
      {({ errors, touched, setFieldValue, values }) => (
        <Form style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SoftBox>
                <Typography variant="h5">Status</Typography>
                <RadioGroup
                  aria-label="notification_preference"
                  name="type"
                  value={values.status_id}
                  row
                >
                  {hasPreUploads && (
                    <FormControlLabel
                      value={6}
                      control={<Radio />}
                      label="Done"
                      onChange={(event) => {
                        const value = (event.target as HTMLInputElement).value;
                        setFieldValue("status_id", Number(value));
                      }}
                    />
                  )}

                  <FormControlLabel
                    value={4}
                    control={<Radio />}
                    label="On-hold"
                    onChange={(event) => {
                      const value = (event.target as HTMLInputElement).value;
                      setFieldValue("status_id", Number(value));
                    }}
                  />

                  <FormControlLabel
                    value={5}
                    control={<Radio />}
                    label="Pending"
                    onChange={(event) => {
                      const value = (event.target as HTMLInputElement).value;
                      setFieldValue("status_id", Number(value));
                    }}
                  />

                  {employeeTicketData?.status_id === 5 && (
                    <FormControlLabel
                      value={3}
                      control={<Radio />}
                      label="In-Progress"
                      onChange={(event) => {
                        const value = (event.target as HTMLInputElement).value;
                        setFieldValue("status_id", Number(value));
                      }}
                    />
                  )}
                </RadioGroup>
              </SoftBox>
            </Grid>

            {values.status_id === 5 && (
              <Grid item xs={12}>
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Pending Reason
                  </SoftTypography>
                  <TextField
                    name="pending_reason"
                    variant="outlined"
                    onChange={(event) => {
                      // Fixed: Removed unnecessary conditional expression
                      setFieldValue(
                        "pending_reason",
                        event.target.value || null
                      );
                    }}
                    fullWidth
                    error={!!errors.pending_reason && touched.pending_reason}
                    helperText={
                      touched.pending_reason && errors.pending_reason
                        ? String(errors.pending_reason)
                        : ""
                    }
                  />
                </SoftBox>
              </Grid>
            )}

            {values.status_id === 4 && (
              <Grid item xs={12}>
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold">
                    On-Hold Reason
                  </SoftTypography>
                  <TextField
                    name="onhold_reason"
                    variant="outlined"
                    onChange={(event) => {
                      setFieldValue(
                        "onhold_reason",
                        event.target.value || null
                      );
                    }}
                    fullWidth
                    error={!!errors.onhold_reason && touched.onhold_reason}
                    helperText={
                      touched.onhold_reason && errors.onhold_reason
                        ? String(errors.onhold_reason)
                        : ""
                    }
                  />
                </SoftBox>
              </Grid>
            )}
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item>
                <SoftButton
                  className="success-btn"
                  variant="gradient"
                  color="success"
                  type="submit"
                >
                  Update
                </SoftButton>
              </Grid>
              <Grid item>
                <SoftButton
                  className="cancel-btn"
                  variant="gradient"
                  color="warning"
                  onClick={onClose}
                >
                  Cancel
                </SoftButton>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default EmployeeEditTicket;
